/* eslint-disable */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {
  Box,
  Checkbox,
  Chip,
  type FilterOptionsState,
  Grid,
  Paper,
  Popper,
  PopperProps,
} from '@mui/material';
import * as CommonIcons from 'assets/common';
import makeStyles from '@mui/styles/makeStyles';
import { Typography } from '../index';
import {
  Control,
  FieldPath,
  FieldValues,
  PathValue,
  RegisterOptions,
  useController,
} from 'react-hook-form';
import {
  classNames,
  getDataPropsFromRest,
  isEmpty,
  isNullOrUndefined,
} from 'utilities/Utils';
import { Cross } from 'assets/modules/daily-steal-deal';
// Type defination for Dropdown
type OptionType = string | Record<string, unknown>;
type FilterOptions = (
  options: unknown[],
  state: FilterOptionsState<unknown>,
) => unknown[];
type AutoCompleteProps = Pick<
  React.ComponentProps<typeof Autocomplete>,
  | 'ref'
  | 'options'
  | 'onBlur'
  | 'placeholder'
  | 'disabled'
  | 'noOptionsText'
  | 'value'
  | 'groupBy'
>;
type RenderOption = (
  props: Object,
  option: OptionType,
  state: {
    inputValue: string;
    selected: boolean;
    labelField?: string;
  },
) => React.ReactNode;
type Variant = {
  isMultiple?: never;
  optionVariant?: 'text' | 'withIcon';
  hierarchicalLabelField?: never;
  maxTags?: never;
  maxTagsText?: never;
  disableClear?: boolean;
  renderOption?: never;
};
type CustomVariant = {
  isMultiple?: boolean;
  optionVariant?: 'custom';
  hierarchicalLabelField?: never;
  maxTags?: never;
  maxTagsText?: never;
  disableClear?: boolean;
  renderOption: RenderOption;
};
type HierarchicalVariantsProps = {
  isMultiple?: never;
  optionVariant?: 'hierarchical';
  hierarchicalLabelField: string;
  maxTags?: never;
  maxTagsText?: never;
  disableClear?: boolean;
  renderOption?: never;
};
type CheckboxVariantsProps = {
  isMultiple: true;
  optionVariant: 'checkbox' | 'tags' | 'multiSelect' | 'chips';
  hierarchicalLabelField?: never;
  maxTags?: number;
  maxTagsText?: string;
  disableClear?: never;
  renderOption?: never;
};
type DropdownProps = Omit<AutoCompleteProps, 'groupBy'> &
  (
    | Variant
    | HierarchicalVariantsProps
    | CheckboxVariantsProps
    | CustomVariant
  ) & {
    options?: AutoCompleteProps['options'];
    searchable?: boolean;
    onChange: (value: unknown, name: string, reason: string) => void;
    value?: AutoCompleteProps['value'];
    isMultiple?: boolean;
    labelField?: string;
    valueField?: string;
    groupByField?: AutoCompleteProps['groupBy'];
    size?: 'small' | 'medium';
    name?: string;
    autoFocus?: boolean;
    limitTags?: number;
    increaseZIndex?: boolean;
    displayRightEl?: (value: unknown) => React.ReactNode;
    extraDeps?: unknown[];
    className?: string;
    popupAnchor?: React.RefObject<HTMLElement>;
    noBorder?: boolean;
    leftBorderRoundedNone?: boolean;
    style?: object;
    popperWidth?: string;
    menuContainer?: React.ComponentType<React.HTMLAttributes<HTMLElement>>;
    leftIcon?: React.ReactNode;
    showSearchIcon?: boolean;
    error?: boolean;
    isInline?: boolean;
    isInlineBorder?: boolean;
    rigthIconColor?: string;
    defaultValues?: unknown[];
    onClick?: () => void;
    isButtonVariant?: boolean;
    rightIcon?: React.ReactNode;
    filterOptions?: FilterOptions;
  };

type ControlledDropdownProps<
  T extends FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
> = Omit<
  DropdownProps,
  'ref' | 'value' | 'onChange' | 'onBlur' | 'name' | 'defaultValue'
> & {
  name: TName; // this name is for useController
  control: Control<T, unknown>;
  defaultValue?: PathValue<T, TName>;
  isRequired?: boolean;
  customValidator?: RegisterOptions<T, TName>['validate'];
};

//  end of type defination for Dropdown
const useStyles = makeStyles(theme => ({
  autocompleteRoot: {
    width: '100%',
  },
  notFaded: {
    '& .Mui-disabled': {
      color: `${theme.palette.text.primary || '#404040'}`, // Keep the text color
      '-webkit-text-fill-color': `${theme.palette.text.primary || '#404040'}`, // For some browsers
      opacity: 1, // Prevent fading
    },
  },
  root: (props: {
    isInlineBorder: boolean;
    zIndexValue: string;
    noBorder: boolean;
    isInline: boolean;
    isCheckboxVariant: boolean;
    isChipVariant: boolean;
    disabled: boolean;
  }) => ({
    '& > .MuiAutocomplete-inputRoot': {
      paddingRight: '28px !important',
    },
    width: '100%',
    height:
      (!props.isInline && props.isCheckboxVariant) || props.isChipVariant
        ? 'full'
        : props.isInlineBorder
        ? 'auto'
        : '40px',
    borderRadius: '4px',
    fontFamily: 'Roboto',
    backgroundColor: props.disabled
      ? `${theme.palette.backgrounds.light || '#F8F8F8'}`
      : `${theme.palette.common.white || '#fff'}`,
    '&:focus-within': {
      '& fieldset[class*="MuiOutlinedInput-notchedOutline"]': {
        border:
          props.noBorder || props.isInline
            ? 'none'
            : '2px solid #3543BF !important',
      },
    },
    '& > div:first-child': {
      minHeight: '40px !important',
      padding:
        props.isCheckboxVariant || props.isChipVariant
          ? '9px 34px 9px 8px !important'
          : '1px',
      backgroundColor: 'inherit',
      gap: props.isCheckboxVariant || props.isChipVariant ? '4px' : '',
      '& input': {
        color: `${theme.palette.text.primary || '#404040'} !important`,
        minWidth: (props.isCheckboxVariant || props.isChipVariant) && '0',
        height: props.isChipVariant && '0',
        '&:focus': {
          minWidth: (props.isCheckboxVariant || props.isChipVariant) && '100%',
          display: (props.isCheckboxVariant || props.isChipVariant) && 'block',
          height: (props.isCheckboxVariant || props.isChipVariant) && '100%',
        },
        padding: props.noBorder
          ? '10px 0px 9px !important'
          : props.isInline
          ? '0px !important'
          : props.isCheckboxVariant || props.isChipVariant
          ? '0 35px 0 0 !important'
          : props.isInlineBorder
          ? '4px 12px 2px !important'
          : '9px 12px 8px !important',
        fontSize: '16px',
      },
      '& .Mui-disabled': {
        color: 'rgba(0, 0, 0, 0.26) !important',
        fontSize: props.isInlineBorder ? '14px' : '16px',
      },
      '& .Mui-disabled:disabled': {
        color: 'rgba(0, 0, 0, 0.26) !important',
        cursor: 'not-allowed',
      },
    },
  }),
  noBorder: {
    '& fieldset[class*="MuiOutlinedInput-notchedOutline"]': {
      border: 'none',
    },
    '& .Mui-disabled': {
      color: `${theme.palette.primary.main || '#3543BF'}`, // Keep the text color
      '-webkit-text-fill-color': `${theme.palette.primary.main || '#3543BF'}`, // For some browsers
      opacity: 1, // Prevent fading
    },
    '& input': {
      color: `${theme.palette.primary.main || '#3543BF'}`,
      fontWeight: '500',
    },
  },
  leftBorderRoundedNone: {
    '& [class*="MuiOutlinedInput-root"]': {
      borderTopLeftRadius: '0px !important',
      borderBottomLeftRadius: '0px !important',
    },
  },
  isInline: {
    '& input': {
      color: `${theme.palette.text.primary || '#404040'}`,
      fontWeight: '400',
      '&::placeholder': {
        color: `${theme.palette.text.secondary || '#787878'}`,
        opacity: 1,
      },
    },
  },
  popper: (props: { zIndexValue }) => ({
    zIndex: props.zIndexValue,
  }),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchable: (props: any) => ({
    paddingLeft:
      props.isCheckboxVariant || props.isChipVariant
        ? '4px !important'
        : '15px !important',
    width: 'auto',
  }),
  paper: {
    backgroundColor: `${theme.palette.common.white || '#FFF'}`,
    padding: '0px 4px 4px 4px',
    boxShadow: '0px 1px 5px rgba(1, 16, 34, 0.1)',
    borderRadius: '4px',
    border: '1px solid #D2D1D1',
    '& .MuiAutocomplete-option.Mui-focused': {
      backgroundColor: `${theme.palette.backgrounds.primary || '#EFF1FF'}`,
    },
    '& .MuiAutocomplete-option[aria-selected="true"]': {
      backgroundColor: `${theme.palette.backgrounds.dark || '#EFEFEF'}`,
      color: `${theme.palette.primary.main || '#3543BF'}`,
    },
    '& li[class^="MuiAutocomplete-option"]': {
      fontSize: '14px',
      fontFamily: 'Roboto',
      lineHeight: '20px',
      padding: '8px 12px',
      marginBottom: '2px',
      backgroundColor: `${theme.palette.common.white || '#FFF'}`,
    },
    '& div[class*="MuiAutocomplete-groupLabel"]': {
      fontSize: '12px !important',
    },
    '& span[class*="MuiCheckbox-root"]': () => ({
      backgroundColor: `${theme.palette.common.white || '#FFF'}`,
    }),
    '& li[data-focus="true"]': {
      backgroundColor: `${theme.palette.backgrounds.primary || '#EFF1FF'}`,
      '& *': {
        color: `${theme.palette.primary.main || '#3543BF'}`,
      },
      '& svg': {
        backgroundColor: 'transparent',
      },
      '& span[class*="MuiCheckbox-root"]': {
        backgroundColor: (props: { isMultiSelectVariant: boolean }) =>
          props.isMultiSelectVariant
            ? `${theme.palette.backgrounds.primary || '#EFF1FF'}`
            : `${theme.palette.backgrounds.light || '#F8F8F8'}`,
        '&.Mui-checked': {
          backgroundColor: (props: { isMultiSelectVariant: boolean }) =>
            props.isMultiSelectVariant
              ? `${theme.palette.backgrounds.primary || '#EFF1FF'}`
              : `${theme.palette.backgrounds.primary || '#EFEFEF'}`,
        },
      },
    },
    '& li[aria-selected="true"]': {
      fontWeight: 500,
      color: `${theme.palette.primary.main || '#3543BF'}`,
      '& *': {
        color: `${theme.palette.primary.main || '#3543BF'}`,
      },
    },
  },
  chip: {
    borderRadius: '60px',
    border: `1px solid ${theme.palette.divider || '#D2D1D1'}`,
    background: '#EFF1FF',
    maxWidth: '180px',
    height: '34px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginRight: (props: { isMultiSelectVariant: boolean }) =>
      props.isMultiSelectVariant ? '0px' : '8px',
    marginBottom: (props: { isMultiSelectVariant: boolean }) =>
      props.isMultiSelectVariant ? '0px' : '12px',
    marginTop: (props: { isMultiSelectVariant: boolean }) =>
      props.isMultiSelectVariant ? '4px' : '0px',
    '& span[class^="MuiChip-label"]': {
      fontSize: '14px !important',
      lineHeight: '22px',
      maxWidth: (props: { isMultiSelectVariant: boolean }) =>
        props.isMultiSelectVariant ? '106px' : '126px',
      padding: (props: { isMultiSelectVariant: boolean }) =>
        props.isMultiSelectVariant && '0px 8px',
    },
    '& svg[class^="MuiChip-deleteIcon-"]': {
      marginRight: (props: { isMultiSelectVariant: boolean }) =>
        props.isMultiSelectVariant ? '8px' : '12px',
    },
    '&:focus': {
      background: `${theme.palette.common.white || '#FFF'}`,
    },
    borderColor: '#3543BF',
  },
  small: {
    width: '182px',
  },
  endAdornment: {
    top: 'auto',
  },
  checkbox: {
    marginRight: '12.5px',
    backgroundColor: `${theme.palette.backgrounds.light || '#F8F8F8'}`,
    color: `${theme.palette.primary.main || '#3543BF'}`,
    padding: (props: { isMultiSelectVariant: boolean }) =>
      props.isMultiSelectVariant && '0px',
  },
  buttonVariant: {
    color: `${theme.palette.backgrounds.white || '#FFF'} !important`,
    '& .MuiInputBase-root': {
      width: '100% !important',
      background: '#3543BF !important',
      '& .MuiInputBase-input': {
        color: `${theme.palette.backgrounds.white || '#FFF'} !important`,
        '-webkit-text-fill-color': `${
          theme.palette.backgrounds.white || '#FFF'
        } !important`,
        fontWeight: '600 !important',
        fontSize: '16px !important',
        borderRight: `1px solid ${theme.palette.backgrounds.white || '#FFF'}`,
        marginRight: '8px',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
  },
}));
function DropdownComp(props: DropdownProps, ref: React.Ref<HTMLInputElement>) {
  const {
    value,
    options,
    onChange,
    onBlur,
    placeholder = 'Select',
    limitTags,
    labelField = 'label',
    valueField = '',
    searchable = false,
    disableClear = false,
    disabled = false,
    noOptionsText = 'No options',
    optionVariant = 'text',
    hierarchicalLabelField,
    isMultiple = false,
    groupByField = null,
    size = 'medium',
    name = '',
    autoFocus = false,
    increaseZIndex = true,
    displayRightEl = null,
    maxTags = Infinity,
    maxTagsText = `You can select upto ${maxTags} items`,
    extraDeps = [],
    className = '',
    popupAnchor,
    noBorder = false,
    leftBorderRoundedNone = false,
    style,
    renderOption,
    popperWidth = null,
    menuContainer,
    leftIcon = null,
    showSearchIcon = true,
    error = false,
    isInline = false,
    isInlineBorder = false,
    rigthIconColor = 'text-text-primary',
    defaultValues = [],
    onClick = () => {},
    isButtonVariant = false,
    rightIcon = null,
    filterOptions = null,
    ...rest
  } = props;
  const [tags, setTags] = useState<unknown[]>([]);
  const [tagOptions, setTagOptions] = useState(options);
  const [displayWarning, setDisplayWarning] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const autoCompleteRef = useRef<HTMLElement | null>(null);
  const isHierarchicalVariant = optionVariant === 'hierarchical';
  const isIconVariant = optionVariant === 'withIcon';
  const isCheckboxVariant = optionVariant === 'checkbox';
  const isMultiSelectVariant = optionVariant === 'multiSelect';
  const isCustom = optionVariant === 'custom';
  const isTagVariant = optionVariant === 'tags';
  const isChipVariant = optionVariant === 'chips';
  const zIndexValue = increaseZIndex ? '100000002 !important' : 'auto';
  const dataTestIdProps = getDataPropsFromRest(rest);
  const isMultipleTagVariant = isMultiple && optionVariant === 'tags';
  const SELECT_ALL_OPTION = { label: 'Select All', value: 'Select All' };
  const filter = createFilterOptions();
  const optionLabel = useCallback(
    item => (typeof item === 'string' ? item : item?.[labelField]),
    [labelField],
  );
  const getFilteredResults = (incomingValue: string) => {
    const results = incomingValue.length
      ? (filter(options as [], {
          inputValue: incomingValue,
          getOptionLabel: optionLabel,
        }) as unknown[])
      : options;
    return results;
  };
  useEffect(() => {
    if (isMultipleTagVariant) {
      setTags(value as unknown[]);
    }
  }, [value]);
  useEffect(() => {
    if (isMultiSelectVariant) {
      setTags(defaultValues);
    }
  }, [defaultValues]);
  useEffect(() => {
    // remove the selected options from the options list
    if (isMultiple && (optionVariant === 'tags' || optionVariant === 'chips'))
      setTagOptions(
        options.filter(option =>
          (value as unknown[])?.every(
            tag => JSON.stringify(tag) !== JSON.stringify(option),
          ),
        ),
      );
  }, [...extraDeps, options]);
  const checkIfAllSelected = () => {
    const tagOptionValues = tagOptions.map(ele => ele[valueField]);
    const tagValues = tags?.map(ele => ele[valueField]);
    return (
      tagOptionValues.every(item => tagValues.indexOf(item) !== -1) &&
      tagOptions.length &&
      tags.length
    );
  };
  const allSelected = checkIfAllSelected();

  const optionRenderer = useCallback(
    (incomingProps, option, state) => {
      if (isHierarchicalVariant) {
        return (
          <div {...incomingProps}>
            <Grid container item direction={'column'}>
              <Typography variant={'body2'} fb={true}>
                {optionLabel(option)}
              </Typography>
              <Typography variant="subtitle1">
                {option[hierarchicalLabelField]}
              </Typography>
            </Grid>
          </div>
        );
      } else if (isIconVariant) {
        return (
          <div {...incomingProps}>
            <CommonIcons.NewSearch style={{ marginRight: '10px' }} />
            {optionLabel(option)}
          </div>
        );
      } else if (isCheckboxVariant || isMultiSelectVariant) {
        return (
          <div {...incomingProps}>
            <Checkbox
              className={'h-[14px] w-[14px] p-0 m-0 mr-2 [&>span>svg]:h-[18px]'}
              checked={state.selected}
            />
            {optionLabel(option)}
          </div>
        );
      } else if (isCustom) {
        return renderOption(incomingProps, option, {
          selected: state.selected,
          inputValue: state.inputValue,
          labelField,
        });
      }
      return <div {...incomingProps}>{optionLabel(option)}</div>;
    },
    [options],
  );
  const classes = useStyles({
    isInlineBorder,
    zIndexValue,
    noBorder,
    isInline,
    isCheckboxVariant,
    disabled,
    isMultiSelectVariant,
    isChipVariant,
  });
  const PaperComponent = useCallback(
    ({ children }) => <Paper className={classes.paper}>{children}</Paper>,
    [],
  );
  const CustomPopper = (popperProps: PopperProps) => {
    const elem = !isNullOrUndefined(popupAnchor)
      ? popupAnchor.current
      : autoCompleteRef.current;
    return (
      <Popper
        {...popperProps}
        style={{ width: popperWidth ? popperWidth : elem?.clientWidth }}
        anchorEl={elem}
      />
    );
  };
  const disableCloseOnSelect =
    (optionVariant === 'tags' &&
      (value as string | unknown[])?.length < maxTags) ||
    optionVariant === 'checkbox' ||
    optionVariant === 'multiSelect' ||
    (optionVariant === 'chips' && isMultiple);
  const handleClearOptions = () => {
    let updatedTags = [];
    if (inputValue.length) {
      updatedTags = tags.filter((tag: unknown) => {
        return !tagOptions.find((tagOption: unknown) => {
          return tag[valueField] === tagOption[valueField];
        });
      });
    }
    setTags([...updatedTags]);
  };
  const handleSelectAll = (isSelected, onChangeCallback) => {
    if (isSelected) {
      const optionsLocal = [...getFilteredResults(inputValue), ...tags];
      const uniqueTags = [
        ...new Map(
          optionsLocal.map((item: unknown) => [item[valueField], item]),
        ).values(),
      ];
      setTags([...uniqueTags]);
      onChangeCallback([...uniqueTags]);
    } else {
      onChange([], name, '');
      handleClearOptions();
    }
  };
  const handleToggleSelectAll = onChangeCallbck => {
    if (typeof handleSelectAll === 'function') {
      handleSelectAll(!allSelected, onChangeCallbck);
    }
  };
  const onToggleOption = selectedOptions => setTags(selectedOptions);
  const handleOnChange = (
    newValue: unknown[],
    incomingName: string,
    reason: string = '',
  ) => {
    if (
      (reason === 'selectOption' || reason === 'removeOption') &&
      isMultiSelectVariant
    ) {
      if (
        newValue.find(option => option[valueField] === SELECT_ALL_OPTION.label)
      ) {
        handleToggleSelectAll(onChange);
      } else {
        onChange(
          newValue.filter(
            option => option[valueField] !== SELECT_ALL_OPTION.label,
          ),
          incomingName,
          reason,
        );
        if (typeof onToggleOption === 'function') {
          onToggleOption(newValue);
        }
      }
      setDisplayWarning(false);
      return;
    }
    if (optionVariant === 'tags' && (value as unknown[])?.length >= maxTags) {
      setDisplayWarning(true);
    } else {
      setDisplayWarning(false);
      onChange(newValue, incomingName, reason);
    }
  };

  const filterOptionsInternal = (_, state) => {
    if (!searchable) {
      state.inputValue = '';
    }

    const results = filter(options as [], state) as unknown[];
    return isMultiSelectVariant ? [SELECT_ALL_OPTION, ...results] : results;
  };
  const handleInputChange = ({ target: { value: val } }) => {
    setInputValue(val);
    if (isMultiSelectVariant) {
      const results = val.length
        ? (filter(options as [], {
            inputValue: val,
            getOptionLabel: optionLabel,
          }) as unknown[])
        : options;
      setTagOptions([...results]);
    }
  };
  const multiSelectOptionRender = (multiSelectOptionProps, option, state) => {
    let selectAllProps = {};
    if (option[valueField] === SELECT_ALL_OPTION.label) {
      selectAllProps = { checked: allSelected };
    }
    return (
      <div {...multiSelectOptionProps}>
        <li>
          <Checkbox
            className={classes.checkbox}
            checked={state.selected}
            {...selectAllProps}
          />
          {option[labelField]}
        </li>
      </div>
    );
  };
  const Tags = ({ tags: incomingTags }) => {
    if (!incomingTags || (incomingTags && incomingTags.length === 0)) {
      return null;
    }
    return (
      <Grid
        container
        direction={'row'}
        className={`${isMultiSelectVariant && 'gap-1'}`}
      >
        {incomingTags?.map((tagsValue, index) => (
          <Grid key={`chip_${index}`}>
            <Chip
              disabled={disabled}
              clickable={false}
              className={classes.chip}
              label={optionLabel(tagsValue)}
              onDelete={() => {
                const newTags = incomingTags.filter(tag => {
                  if (valueField) {
                    return tag[valueField] !== tagsValue[valueField];
                  }
                  return optionLabel(tag) !== optionLabel(tagsValue);
                });
                setTags(newTags as []);
                onChange(newTags, name, '');
                setTagOptions(
                  options.filter(option =>
                    (newTags as unknown[]).every(
                      tag => JSON.stringify(tag) !== JSON.stringify(option),
                    ),
                  ),
                );
              }}
              deleteIcon={
                <CommonIcons.Close
                  style={{ width: '16px', height: '16px' }}
                  data-test-id={`${optionLabel(tagsValue)}_chip_cross`}
                />
              }
            />
          </Grid>
        ))}
        {displayWarning &&
          maxTagsText &&
          maxTags !== Infinity &&
          (value as unknown[])?.length >= maxTags && (
            <Grid xs={12}>
              <Box my={1}>
                <Typography themeColor="error.main">{maxTagsText}</Typography>
              </Box>
            </Grid>
          )}
      </Grid>
    );
  };
  const autoCompleteProps: { inputValue?: string } = {};
  if (isMultiSelectVariant) {
    autoCompleteProps.inputValue = inputValue;
  }
  const CheckboxChip = ({ incomingChipValue, option }) => {
    const wprClasses = [
      'flex items-center gap-1 rounded-full border border-solid border-[1px] px-[6px]',
      disabled
        ? 'bg-color-light border-color-divider pointer-events-none'
        : 'border-primary-light bg-color-primary',
      isMultiple ? 'ml-2' : '',
    ];

    return (
      <div className={wprClasses.join(' ')}>
        <Typography
          variant="body1"
          className="max-w-[100px] text-ellipsis overflow-hidden whitespace-nowrap"
        >
          {optionLabel(option)}
        </Typography>
        <Cross
          className="cursor-pointer"
          height={10}
          width={10}
          onClick={() => {
            const newTags = incomingChipValue.filter(
              tag => optionLabel(tag) !== optionLabel(option),
            );
            onChange(newTags, name, '');
          }}
          aria-disabled={disabled}
          stroke={disabled ? '#D2D1D1' : '#3543BF'}
        />
      </div>
    );
  };
  return (
    <>
      {isTagVariant ? <Tags tags={tags} /> : null}
      <Autocomplete
        onClick={onClick}
        classes={{
          root: classes.autocompleteRoot,
          popper: classes.popper,
          endAdornment: classes.endAdornment,
        }}
        openOnFocus
        ref={autoCompleteRef}
        value={
          isMultiSelectVariant
            ? tags // @ts-ignore
            : value?.value === null || isEmpty(value)
            ? isMultiple
              ? []
              : ''
            : value
        }
        style={style}
        limitTags={limitTags}
        options={
          optionVariant === 'tags' || optionVariant === 'multiSelect'
            ? tagOptions
            : options
        }
        onChange={(_, newValue: unknown[], reason) =>
          handleOnChange(newValue, name, reason)
        }
        onBlur={e => {
          if (isMultiSelectVariant) {
            setTagOptions(getFilteredResults(inputValue));
          } else {
            setTagOptions(
              options.filter(option =>
                (tags as unknown[]).every(
                  tag => JSON.stringify(tag) !== JSON.stringify(option),
                ),
              ),
            );
          }
          if (typeof onBlur === 'function') {
            onBlur(e);
          }
        }}
        getOptionLabel={optionLabel}
        popupIcon={
          rightIcon || (
            <CommonIcons.ChevronDownUnfilled
              className={classNames(disabled ? '' : rigthIconColor)}
            />
          )
        }
        clearIcon={<CommonIcons.CloseSmall />}
        disableClearable={isMultiple ? true : disableClear}
        PaperComponent={PaperComponent}
        ListboxComponent={menuContainer}
        PopperComponent={CustomPopper}
        disabled={disabled}
        selectOnFocus={false}
        noOptionsText={noOptionsText}
        renderInput={params => {
          const InputProps = {
            ...params.InputProps,
            readOnly: !searchable,
            ...(leftIcon && { startAdornment: leftIcon }),
          };
          if (
            showSearchIcon &&
            ((searchable && !isMultiple) ||
              optionVariant === 'tags' ||
              optionVariant === 'multiSelect')
          ) {
            // Hack to not display chips when optionVariant is tags or multiSelect
            InputProps.startAdornment = (
              <CommonIcons.NewSearch className={classes.searchable} />
            );
          }
          if (!isNullOrUndefined(displayRightEl)) {
            InputProps.endAdornment = (
              <>
                {displayRightEl(value)}
                {InputProps.endAdornment}
              </>
            );
          }

          return (
            <TextField
              {...params}
              autoFocus={autoFocus}
              inputRef={ref}
              InputProps={InputProps}
              placeholder={isChipVariant && isMultiple ? '' : placeholder}
              variant="outlined"
              error={error}
              disabled={!searchable}
              style={{ caretColor: !searchable ? 'transparent' : 'unset' }}
              onChange={handleInputChange}
              className={classNames(
                classes.root,
                classes.notFaded,
                size === 'small' && classes.small,
                className,
                (noBorder || isInline) && classes.noBorder,
                leftBorderRoundedNone && classes.leftBorderRoundedNone,
                isInline && classes.isInline,
                isButtonVariant && classes.buttonVariant,
              )}
            />
          );
        }}
        groupBy={groupByField}
        renderOption={
          optionVariant !== 'multiSelect'
            ? optionRenderer
            : multiSelectOptionRender
        }
        multiple={isMultiple}
        disableCloseOnSelect={disableCloseOnSelect}
        isOptionEqualToValue={(option, incValue) => {
          return typeof incValue === 'string'
            ? option === incValue
            : valueField
            ? option[valueField] === incValue?.[valueField]
            : option[labelField] === incValue?.[labelField];
        }}
        renderTags={tagValue =>
          tagValue.map(option => (
            <CheckboxChip incomingChipValue={tagValue} option={option} />
          ))
        }
        filterOptions={
          typeof filterOptions === 'function'
            ? filterOptions
            : filterOptionsInternal
        }
        {...autoCompleteProps}
        {...dataTestIdProps}
      />
      {isMultiSelectVariant ? <Tags tags={tags} /> : null}
    </>
  );
}
const Dropdown = forwardRef(DropdownComp);
const ControlledDropdown = <
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>(
  props: ControlledDropdownProps<T, TName>,
) => {
  const {
    name,
    control,
    defaultValue = null,
    isRequired = false,
    customValidator,
    ...rest
  } = props;
  const validations: Partial<RegisterOptions<T, TName>> = {
    required: {
      value: isRequired,
      message: 'This field is required',
    },
    validate: customValidator,
  };
  const {
    field: { ref, value, onChange, onBlur },
  } = useController<T, TName>({
    name,
    control,
    defaultValue,
    rules: validations,
  });

  return (
    <Dropdown
      ref={ref}
      value={value?.value === 'null' ? '' : value}
      onChange={onChange}
      onBlur={onBlur}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...(rest as any)}
    />
  );
};

export { Dropdown, ControlledDropdown };
